import {
  property,
  supertypeClass,
  remote,
  Persistable,
  Remoteable,
  Supertype,
  amorphicStatic,
} from '@haventech/amorphic';
import { Policy } from './Policy';
import { RiderType } from '../static/Product';
import { FlatExtra } from './FlatExtra';
import { Utils } from '../Utils';
import { PremiumBreakdown } from '../../../common/js/customer/PremiumBreakdown';
import { PolicyAmendmentProperty, PolicyAmendmentSubProperty } from '../static/PolicyAmendmentProperty';
import { FlatExtraAmendments } from '../static/FlatExtraAmendments';
import { Exchange1035 } from '../quote/coverpath/Exchange1035';

@supertypeClass({ toClient: true, toServer: true })
export class PolicyRider extends Remoteable(Persistable(Supertype)) {
  @property()
  type: string;

  @property()
  amount: number;

  @property()
  premiumBase: number;

  @property()
  premiumWaiver: number;

  @property({
    getType: () => Policy,
    comment: 'Policy to which this rider is attached.',
  })
  policy: Policy;

  @property({ type: Array, getType: () => FlatExtra })
  flatExtras: Array<FlatExtra> = [];

  @property({
    comment: 'ALIR has scheduled payments',
  })
  hasScheduledPayments: boolean;

  @property({
    comment: 'Planned years additional life insurance is purchased for',
  })
  plannedYears: number;

  @property({
    comment: 'ALIR Payment Frequency',
  })
  paymentFrequency: string;

  @property({
    comment: 'Has a non-1035 lump sum',
  })
  hasLumpSum: boolean;

  @property({
    comment: 'ALIR lump sum amount',
  })
  lumpSumAmount: number;

  @property({
    comment: 'Lump sum uses a 1035',
  })
  lumpSumUses1035: boolean;

  @property({
    comment: '1035 Exchange reference',
  })
  exchange1035: Exchange1035;

  @property({
    comment: 'CIR Payout method',
  })
  benefitDisbursementMethod: number;

  @property({
    comment: 'Predicted age the insured begins taking the CIR benefit',
  })
  predictedBenefitAge: number;

  @property({
    comment: 'Include PUA in CIR benefit pool',
  })
  puaAddedToBenefitPool: boolean;

  @property({
    comment: 'Usage of PUA in the CIR benefit pool',
  })
  monthlyIncreaseOption: boolean;

  get exchangeAmount1035(): number {
    return this.exchange1035 && this.exchange1035.cashValueTransfer;
  }

  get descriptiveType() {
    return `${this.type} ${this.description}`;
  }

  get description() {
    if (this.type === RiderType.GIR) {
      return `Guaranteed Insurability Rider`;
    } else if (this.type === RiderType.RTR) {
      return `Renewable Term Rider`;
    } else if (this.type === RiderType.ALIR) {
      return `Additional Life Insurance Rider`;
    } else if (this.type === RiderType.CIR) {
      return `Chronic Illness Rider`;
    }
  }

  get formattedPremiumBase() {
    return Utils.formatCurrency(this.premiumBase, 2);
  }

  get formattedAmount() {
    return Utils.formatCurrency(this.amount, 2);
  }

  constructor() {
    super();
  }

  static new(type: RiderType, policy: Policy) {
    return new PolicyRider().init(type, policy);
  }

  init(type: RiderType, policy: Policy) {
    this.type = type;
    this.policy = policy;
    return this;
  }

  remove() {
    this.flatExtras.forEach((flatExtra) => flatExtra.persistDelete());
    this.flatExtras = [];
    if (this.exchange1035) {
      this.remove1035();
    }
  }

  update(premiumBase: number, premiumWaiver: number, flatExtras: Array<FlatExtra>) {
    this.updateBase(premiumBase);
    this.premiumWaiver = this.policy.waiver ? Utils.round(premiumWaiver, 2) : 0;
    const newFE = this.flatExtras;
    (flatExtras || []).forEach((fe) => {
      const currentFE = this.flatExtras.find((flatExtra) => flatExtra.ruleCode === fe.ruleCode);
      if (currentFE) {
        currentFE.amount = fe.amount;
        currentFE.premium = fe.premium;
      } else {
        newFE.push(
          FlatExtra.new(fe.name, fe.amount, this.policy, null, fe.ruleCode, fe.premium, fe.expiryAfterInYears, this)
        );
      }
    });

    this.flatExtras = newFE;
  }

  updateBase(premiumBase: number): void {
    this.premiumBase = premiumBase;
  }

  getTotalFlatExtras(): number {
    return PremiumBreakdown.getTotalFlatExtras(this.flatExtras);
  }

  getTotalFlatExtrasWithPendingAmendment(): number {
    return PremiumBreakdown.getTotalFlatExtras(this.flatExtrasWithPendingAmendment());
  }

  private withPendingAmendment(prop: PolicyAmendmentSubProperty): string | number | boolean | Date | FlatExtra[] {
    const pendingPCH = this.policy.getPendingPolicyChange(`${this.type}.${prop}`);

    if (pendingPCH) {
      return pendingPCH.getTypedNewValue();
    }

    return prop.split('.').reduce((start, current) => (start ? start[current] : null), this);
  }

  amountWithPendingAmendment(): number {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_PREMIUM_AMOUNT) as number;
  }

  premiumBaseWithPendingAmendment(): number {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_PREMIUM_BASE) as number;
  }

  premiumWaiverWithPendingAmendment(): number {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_PREMIUM_WAIVER) as number;
  }

  benefitDisbursementMethodWithPendingAmendment(): number {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.BENEFIT_DISBURSEMENT_METHOD) as number;
  }

  puaAddedToBenefitPoolWithPendingAmendment(): boolean {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.PUA_ADDED_TO_BENEFIT_POOL) as boolean;
  }

  monthlyIncreaseOptionWithPendingAmendment(): boolean {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.MONTHLY_INCREASE_OPTION) as boolean;
  }

  predictedBenefitAgeWithPendingAmendment(): number {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.PREDICTED_BENEFIT_AGE) as number;
  }

  plannedYearsWithPendingAmendment(): number {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_PLANNED_YEARS) as number;
  }

  paymentFrequencyWithPendingAmendment(): string {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_PAYMENT_FREQUENCY) as string;
  }

  hasAlirScheduledPaymentsWithPendingAmendment(): boolean {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_HAS_ALIR_SCHEDULED_PAYMENTS) === true;
  }

  hasAlir1035ExchangeWithPendingAmendment(): boolean {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_HAS_ALIR_1035_EXCHANGE) === true;
  }

  alir1035ExchangeAmountWithPendingAmendment(): number {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_ALIR_1035_CASH_VALUE_TRANSFER) as number;
  }

  alir1035CostBasisWithPendingAmendment(): number {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_ALIR_1035_COST_BASIS) as number;
  }

  alir1035PolicyTypeWithPendingAmendment(): string {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_ALIR_1035_POLICY_TYPE) as string;
  }

  alir1035ApplyTowardsWithPendingAmendment(): string {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_ALIR_1035_APPLY_TOWARDS) as string;
  }

  alir1035ExpectedArrivalWithPendingAmendment(): number {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_ALIR_1035_EXPECTED_ARRIVAL) as number;
  }

  alir1035IsMecWithPendingAmendment(): boolean {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_ALIR_1035_IS_MEC) as boolean;
  }

  alir1035FundsReceivedDateWithPendingAmendment(): Date {
    return this.withPendingAmendment(PolicyAmendmentSubProperty.RIDER_ALIR_1035_FUNDS_RECEIVED_DATE) as Date;
  }

  flatExtrasWithPendingAmendment(): Array<FlatExtra> {
    return FlatExtraAmendments.fetchRiderFlatExtrasWithPendingAmendment(
      this.policy,
      this.flatExtras,
      `${this.type}.${PolicyAmendmentSubProperty.RIDER_FLAT_EXTRA}` as PolicyAmendmentProperty
    );
  }

  @remote({ on: 'server' })
  async remove1035(): Promise<void> {
    if (this.exchange1035) {
      const transaction = amorphicStatic.begin();
      const exchange1035 = this.exchange1035;
      this.exchange1035 = null;
      this.setDirty(transaction);
      this.persistorSave({ transaction });
      await exchange1035.persistorDelete({ transaction });
      await amorphicStatic.end(transaction);
    }
    return Promise.resolve();
  }
}
